import type { ColumnDef } from "@tanstack/react-table";
import { Building2, Plus, RefreshCw, Wrench } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppHeader, type AppHeaderConfig } from "@/components/app-header";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import {
  ListSearch,
  ListToolbar,
  SortMenu
} from "@/components/ui/list-toolbar";
import { PageHeader } from "@/components/ui/page-header";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";

import { LawFirmsAPI } from "../../../api/lawFirms";
import { type LawFirm } from "../../../api/types/lawFirm";
import { ProvisionNewFirmForm } from "./ProvisionNewFirmForm";

const LAW_FIRMS_HEADER: AppHeaderConfig = {
  crumbs: [
    { id: "internal-tools", label: "Internal Tools", icon: Wrench },
    { id: "law-firms", label: "Law Firms", current: true, icon: Building2 }
  ]
};

type SortFilter = "newest" | "oldest" | "nameAsc" | "nameDesc";

const SORT_OPTIONS: Array<{ value: SortFilter; label: string }> = [
  { value: "newest", label: "Newest Joined" },
  { value: "oldest", label: "Oldest Joined" },
  { value: "nameAsc", label: "Name (A → Z)" },
  { value: "nameDesc", label: "Name (Z → A)" }
];

export function LawFirmsList() {
  const PAGE_LIMIT = 50;
  const TABLE_INFINITE_SCROLL_THRESHOLD_PX = 200;
  useAppHeader(LAW_FIRMS_HEADER);
  const navigate = useNavigate();

  const [items, setItems] = useState<LawFirm[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearchValue] = useState("");
  const searchTimeoutRef = useRef<number | null>(null);
  const requestSeqRef = useRef(0);
  const appendNextRef = useRef(false);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const infiniteScrollInFlightRef = useRef(false);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [sortFilter, setSortFilter] = useState<SortFilter>("newest");

  const [isProvisionDrawerOpen, setIsProvisionDrawerOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchList = useCallback(async () => {
    requestSeqRef.current += 1;
    const seq = requestSeqRef.current;
    const shouldAppend = appendNextRef.current && offset > 0;
    appendNextRef.current = false;

    if (shouldAppend) setIsLoadingMore(true);
    else setIsLoadingList(true);
    setError(null);

    try {
      const sortParams =
        sortFilter === "oldest"
          ? { orderBy: "created_at", descending: false }
          : sortFilter === "nameAsc"
          ? { orderBy: "name", descending: false }
          : sortFilter === "nameDesc"
          ? { orderBy: "name", descending: true }
          : { orderBy: "created_at", descending: true };

      const res = await LawFirmsAPI.adminList({
        limit: PAGE_LIMIT,
        pageToken: String(offset),
        q: search.trim() ? search.trim() : undefined,
        ...sortParams
      });
      if (requestSeqRef.current !== seq) return;

      const page = Array.isArray(res.lawFirms) ? res.lawFirms : [];
      setItems((prev) => (shouldAppend ? [...prev, ...page] : page));
      setTotalCount(typeof res.totalCount === "number" ? res.totalCount : 0);
      setHasMore(Boolean(res.nextPageToken));
    } catch (err) {
      if (requestSeqRef.current !== seq) return;
      const e =
        err instanceof Error ? err : new Error("Failed to load law firms");
      setError(e);
      if (!shouldAppend) {
        setItems([]);
        setTotalCount(0);
      }
    } finally {
      if (requestSeqRef.current === seq) {
        if (shouldAppend) setIsLoadingMore(false);
        else setIsLoadingList(false);
        infiniteScrollInFlightRef.current = false;
      }
    }
  }, [PAGE_LIMIT, offset, search, sortFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshNonce]);

  const resetToFirstPage = useCallback(() => {
    appendNextRef.current = false;
    infiniteScrollInFlightRef.current = false;
    setOffset(0);
    setHasMore(true);
    setItems([]);
    setTotalCount(0);
  }, []);

  const refresh = useCallback(() => {
    resetToFirstPage();
    setRefreshNonce((n) => n + 1);
  }, [resetToFirstPage]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      resetToFirstPage();
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = window.setTimeout(() => {
        setSearchValue(value);
      }, 300);
    },
    [resetToFirstPage]
  );

  const handleSortChange = useCallback(
    (value: SortFilter) => {
      setSortFilter(value);
      resetToFirstPage();
    },
    [resetToFirstPage]
  );

  const loadMore = useCallback(() => {
    if (error) return;
    if (!hasMore) return;
    if (isLoadingMore) return;
    if (infiniteScrollInFlightRef.current) return;
    infiniteScrollInFlightRef.current = true;
    appendNextRef.current = true;
    setOffset(items.length);
  }, [error, hasMore, isLoadingMore, items.length]);

  const maybeLoadMore = useCallback(() => {
    const el = tableScrollContainerRef.current;
    if (!el) return;
    if (error) return;
    if (!hasMore) return;
    if (isLoadingMore) return;
    if (isLoadingList) return;
    if (infiniteScrollInFlightRef.current) return;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceFromBottom > TABLE_INFINITE_SCROLL_THRESHOLD_PX) return;

    loadMore();
  }, [
    TABLE_INFINITE_SCROLL_THRESHOLD_PX,
    error,
    hasMore,
    isLoadingList,
    isLoadingMore,
    loadMore
  ]);

  useEffect(() => {
    if (isLoadingList) return;
    maybeLoadMore();
  }, [isLoadingList, items.length, maybeLoadMore]);

  const formatJoinedLabel = (createdAt?: string | null) => {
    if (!createdAt) return "—";
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString(undefined, { month: "short", year: "numeric" });
  };

  const formatCreditBalanceLabel = (creditBalance?: number | null) => {
    if (typeof creditBalance !== "number" || !Number.isFinite(creditBalance)) {
      return "—";
    }
    return creditBalance.toLocaleString(undefined, {
      maximumFractionDigits: 0
    });
  };

  const locationOf = (firm: LawFirm) =>
    [firm.city, firm.state].filter(Boolean).join(", ") || "—";

  const columns: ColumnDef<LawFirm, unknown>[] = [
    {
      id: "firm",
      header: "Firm",
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar size="md" shape="square">
            <AvatarFallback>
              {row.original.name.slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-base text-fg">
              {row.original.name}
            </span>
            <span className="truncate text-sm text-fg-3">
              {locationOf(row.original)}
            </span>
          </div>
        </div>
      )
    },
    {
      id: "joined",
      header: "Joined",
      cell: ({ row }) => (
        <span className="text-base whitespace-nowrap text-fg-2">
          {formatJoinedLabel(row.original.created_at)}
        </span>
      )
    },
    {
      id: "credits",
      header: "Credit Balance",
      cell: ({ row }) => (
        <span className="text-base whitespace-nowrap text-fg-2 tabular-nums">
          {formatCreditBalanceLabel(row.original.credit_balance)}
        </span>
      )
    }
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <div className="flex shrink-0 flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Law Firms"
          count={totalCount || null}
          description={
            totalCount > 0
              ? `Showing ${items.length} of ${totalCount}`
              : undefined
          }
          actions={
            <Button size="sm" onClick={() => setIsProvisionDrawerOpen(true)}>
              <Plus /> Provision New Firm
            </Button>
          }
        />
        <ListToolbar>
          <ListSearch
            aria-label="Search firms"
            placeholder="Search Firms"
            value={searchInput}
            onValueChange={setSearch}
          />
          <SortMenu
            options={SORT_OPTIONS}
            value={sortFilter}
            onValueChange={(v) => handleSortChange(v as SortFilter)}
          />
          <div className="ml-auto">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Refresh"
              onClick={refresh}
            >
              <RefreshCw />
            </Button>
          </div>
        </ListToolbar>
      </div>

      <div
        ref={tableScrollContainerRef}
        onScroll={maybeLoadMore}
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        {!isLoadingList && items.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={error ? "Couldn't Load Law Firms" : "No Matching Law Firms"}
            description={error?.message}
            action={
              error ? (
                <Button variant="secondary" size="sm" onClick={refresh}>
                  Try Again
                </Button>
              ) : undefined
            }
            className="py-16"
          />
        ) : (
          <DataTable
            data={items}
            columns={columns}
            rowHeight="double"
            getRowId={(f) => f.uid}
            onRowClick={(f) => navigate(`/admin/law-firms/${f.uid}`)}
            isLoading={isLoadingList}
            className="overflow-visible"
          />
        )}
        {isLoadingMore ? (
          <div className="flex justify-center px-4 py-4">
            <Spinner />
          </div>
        ) : null}
      </div>

      <Sheet
        open={isProvisionDrawerOpen}
        onOpenChange={setIsProvisionDrawerOpen}
      >
        <SheetContent
          side="right"
          className="flex w-[min(900px,95vw)] flex-col gap-0 p-0 sm:max-w-none"
        >
          <SheetHeader className="border-b border-line-1">
            <SheetTitle>Provision New Firm</SheetTitle>
            <SheetDescription>
              Create a new firm account with subscription details.
            </SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col overflow-auto bg-surface">
            <ProvisionNewFirmForm
              variant="drawer"
              onCancel={() => setIsProvisionDrawerOpen(false)}
              onCreated={(lawFirmUid) => {
                setIsProvisionDrawerOpen(false);
                navigate(`/admin/law-firms/${lawFirmUid}`);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
